/* assets/cadre-launch-offer.js
   Cadre Launch Offer: enhance Appstle purchase options + CTA text
   Fixes desktop click (overlay interception) by selecting input from wrapper, not label.
   Robust against variant changes without hover/mutation thrashing.
*/
(function () {
  // ---- Config ----
  const PAGE_SCOPE_SELECTOR = ".product-actions.cadre-launch-offer";
  const WIDGET_SELECTOR = ".appstle_sub_widget";
  const CTA_TEXT_SELECTOR = 'span[data-js-product-add-to-cart-text]';
  const CTA_SUBSCRIBE = "Get My Free First Month";
  const CTA_ONETIME = "Buy One Bag";

  // bump this if you change markup; used to force-refresh injected HTML
  const CADRE_VERSION = "3";

  // ---- Utils ----
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const cleanMoney = (s) => {
    if (!s) return "";
    const m = String(s).match(/\$?\s*[\d,.]+/);
    return m ? m[0].replace(/\s+/g, "") : String(s).trim();
  };

  const isLaunchOfferPage = () => !!$(PAGE_SCOPE_SELECTOR);
  const getScope = () => $(PAGE_SCOPE_SELECTOR);

  function getSelectedMode(widget) {
    const checked = $('input[name="selling_plan"]:checked', widget);
    return checked && checked.value === "" ? "onetime" : "subscribe";
  }

  function updateCTA(widget) {
    const scope = getScope();
    if (!scope) return;
    const ctaSpan = $(CTA_TEXT_SELECTOR, scope);
    if (!ctaSpan) return;

    const mode = getSelectedMode(widget);
    ctaSpan.textContent = mode === "onetime" ? CTA_ONETIME : CTA_SUBSCRIBE;

    // also stamp state classes for CSS if you want
    widget.classList.toggle("cadre-mode--subscribe", mode === "subscribe");
    widget.classList.toggle("cadre-mode--onetime", mode === "onetime");

    // mark wrappers
    const wrappers = $$(".widgetSellingPlanWrapper .appstle_input_wrapper", widget);
    wrappers.forEach((w) => {
      const inp = $('input[type="radio"][name="selling_plan"]', w);
      w.classList.toggle("cadre-selected", !!inp && inp.checked);
    });
  }

  function readPrices(widget) {
    const subCompare = cleanMoney($(".appstle_sellingPlan_formattedCompareAtPrice", widget)?.textContent);
    const subPrice = cleanMoney($(".appstle_sellingPlan_price.appstle_price_block", widget)?.textContent);

    const oneTime =
      cleanMoney($(".appstle_onetime_amount", widget)?.textContent) ||
      cleanMoney($(".appstle_sellingPlan_price.appstle_onetime_amount", widget)?.textContent);

    return {
      subCompare: subCompare || "$39.00",
      subPrice: subPrice || "$39.00",
      oneTime: oneTime || subCompare || "$52.00",
    };
  }

  function signatureFrom(prices) {
    return `${CADRE_VERSION}|${prices.subCompare}|${prices.subPrice}|${prices.oneTime}`;
  }

  function buildSubscriptionCard({ subCompare, subPrice }) {
    return `
      <div class="cadre-card cadre-card--sub" data-cadre-version="${CADRE_VERSION}">
        <div class="cadre-card__row">
          <div class="cadre-card__left">
            <div class="cadre-card__kicker">Monthly Subscription</div>
            <div class="cadre-card__headline">First Month FREE</div>
            <div class="cadre-card__sub">Just pay shipping · Cancel anytime</div>
          </div>

          <div class="cadre-card__pricebox" aria-label="Price">
            <div class="cadre-card__price-now">
              <span class="cadre-card__strike">${subCompare}</span>
              <span class="cadre-card__free">$0</span>
            </div>
            <div class="cadre-card__price-note">today + shipping</div>
          </div>
        </div>

        <div class="cadre-card__meta">
          <div class="cadre-card__meta-line">
            <strong>Month 2+:</strong> ${subPrice} / month <span class="cadre-card__muted">(25% off for life)</span>
          </div>

          <ul class="cadre-card__bullets">
            <li>25% off future orders</li>
            <li>Cancel or pause anytime</li>
          </ul>

          <div class="cadre-card__micro">No second charge unless you stay subscribed.</div>
        </div>
      </div>
    `;
  }

  function buildOnetimeCard({ oneTime }) {
    return `
      <div class="cadre-card cadre-card--one" data-cadre-version="${CADRE_VERSION}">
        <div class="cadre-card__row">
          <div class="cadre-card__left">
            <div class="cadre-card__kicker">One-time</div>
            <div class="cadre-card__headline">Buy Once</div>
            <div class="cadre-card__sub">No free month · No subscription savings</div>
          </div>

          <div class="cadre-card__pricebox" aria-label="Price">
            <div class="cadre-card__price-alone">${oneTime}</div>
            <div class="cadre-card__price-note">one-time</div>
          </div>
        </div>
      </div>
    `;
  }

  // ---- Desktop click fix (overlay-proof) ----
  // Select the input directly from wrapper, regardless of what element got clicked.
  function bindForceSelect(wrapper) {
    if (!wrapper || wrapper.dataset.cadreClickBound === "1") return;
    wrapper.dataset.cadreClickBound = "1";

    const selectThisWrapper = () => {
      const input = $('input[type="radio"][name="selling_plan"]', wrapper);
      if (!input || input.checked) return;

      input.checked = true;
      // fire both for maximum compatibility with Appstle/theme listeners
      input.dispatchEvent(new Event("input", { bubbles: true }));
      input.dispatchEvent(new Event("change", { bubbles: true }));

      // update visuals/CTA immediately
      const widget = wrapper.closest(WIDGET_SELECTOR);
      if (widget) updateCTA(widget);
    };

    // pointerdown catches cases where click never bubbles due to overlays / prevented click
    wrapper.addEventListener("pointerdown", (e) => {
      // Only left click / touch
      if (e.pointerType === "mouse" && e.button !== 0) return;
      selectThisWrapper();
    }, true);

    // click as a backup
    wrapper.addEventListener("click", (e) => {
      selectThisWrapper();
    }, true);
  }

  function enhanceWidget(widget) {
    if (!widget || !isLaunchOfferPage()) return;

    // Normalize title
    const title = $(".appstle_widget_title", widget);
    if (title) title.textContent = "Choose your option";

    const prices = readPrices(widget);
    const sig = signatureFrom(prices);

    // Find wrappers
    const wrappers = $$(".widgetSellingPlanWrapper .appstle_input_wrapper", widget);
    if (!wrappers.length) return;

    const subWrap = wrappers.find((w) => !w.classList.contains("appstle_onetime_block"));
    const oneWrap = wrappers.find((w) => w.classList.contains("appstle_onetime_block"));
    const subLabel = subWrap ? $("label", subWrap) : null;
    const oneLabel = oneWrap ? $("label", oneWrap) : null;

    // Detect if our current injected markup is outdated
    const existingSubVersion = $(".cadre-card--sub", subLabel || widget)?.getAttribute("data-cadre-version");
    const existingOneVersion = $(".cadre-card--one", oneLabel || widget)?.getAttribute("data-cadre-version");
    const needsRefresh =
      widget.dataset.cadreEnhanced !== "1" ||
      widget.dataset.cadreSig !== sig ||
      existingSubVersion !== CADRE_VERSION ||
      existingOneVersion !== CADRE_VERSION;

    if (needsRefresh) {
      widget.classList.add("cadre-enhanced");
      widget.dataset.cadreEnhanced = "1";
      widget.dataset.cadreSig = sig;

      if (subLabel) subLabel.innerHTML = buildSubscriptionCard(prices);
      if (oneLabel) oneLabel.innerHTML = buildOnetimeCard(prices);
    }

    // Bind input change once
    $$('input[name="selling_plan"]', widget).forEach((inp) => {
      if (inp.dataset.cadreBound === "1") return;
      inp.dataset.cadreBound = "1";
      inp.addEventListener("change", () => updateCTA(widget), true);
      inp.addEventListener("input", () => updateCTA(widget), true);
    });

    // Bind overlay-proof selection
    wrappers.forEach(bindForceSelect);

    // Ensure CTA reflects current selection
    updateCTA(widget);
  }

  function enhanceAll() {
    if (!isLaunchOfferPage()) return;
    const scope = getScope();
    if (!scope) return;
    $$(WIDGET_SELECTOR, scope).forEach(enhanceWidget);
  }

  // ---- Observer (low thrash) ----
  let obs = null;
  function startObserver() {
    if (!isLaunchOfferPage()) return;
    const scope = getScope();
    if (!scope) return;

    const schedule = () => {
      clearTimeout(window.__cadreEnhanceT);
      window.__cadreEnhanceT = setTimeout(enhanceAll, 80);
    };

    obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "childList") {
          const added = Array.from(m.addedNodes || []);
          const removed = Array.from(m.removedNodes || []);

          const relevant = [...added, ...removed].some((n) => {
            if (!(n instanceof HTMLElement)) return false;
            return (
              n.matches?.(WIDGET_SELECTOR) ||
              n.querySelector?.(WIDGET_SELECTOR) ||
              n.querySelector?.('input[name="selling_plan"]') ||
              n.querySelector?.(".widgetSellingPlanWrapper")
            );
          });

          if (relevant) {
            schedule();
            return;
          }
        }

        if (m.type === "attributes" && m.target instanceof HTMLElement) {
          if (m.target.matches(WIDGET_SELECTOR) && m.attributeName === "data-selected-selling-plan") {
            schedule();
            return;
          }
        }
      }
    });

    obs.observe(scope, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["data-selected-selling-plan"],
    });

    // Variant picker changes (theme-dependent selectors)
    document.addEventListener(
      "change",
      (e) => {
        const t = e.target;
        if (!t) return;
        if (
          t.matches('select[name="id"]') ||
          t.matches('input[name="id"]') ||
          t.matches('[name="options[]"]') ||
          t.closest(".variant-picker") ||
          t.closest("[data-variant-picker]") ||
          t.closest(".product-variants")
        ) {
          setTimeout(enhanceAll, 120);
        }
      },
      true
    );
  }

  // Init
  startObserver();
  enhanceAll();
  setTimeout(enhanceAll, 250);
  setTimeout(enhanceAll, 900);
})();
